import { toast } from 'react-toastify';
import { Modal, ModalContent } from '@ds';
import { Form } from '@ui';
import { isNil } from 'lodash-es';
import { buildDefaultTxUnassign, buildUnassignTxUsersIfDA } from '@features/components/CaseFormBlocks';
import { getLateralityPatch } from '@features/forms/CaseForms/schema';
import { useUpdateCaseMutation, useQuestionaryEditAllowanceMutation } from '@/queries/case';
import { EditGeneralCaseForm } from './EditGeneralCaseForm';
import { CaseGeneralFormType, caseGeneralInitialValues, caseGeneralSchema } from './config';
import { EditCaseModalFooter } from '../components/EditCaseModalFooter';
import {
  getChangedValues,
  getClinicalRecordByType,
  getQuestionaryEditAllowanceChange,
  replaceEmptyWithNull,
} from '@/utils/helpers';
import { Assignee, Case, ClinicalItemType, ServiceType } from '@/api/apis/case';
import { useSetNrp } from '@/hooks/useSetNrp';
import { retrieveUnifiedError } from '@/api/utils';
import { useReallocationFilesTransfer } from '@/queries/file';

type EditGeneralCaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  caseData: Case;
  routeProviders: string[];
};

export const EditGeneralCaseModal = ({ isOpen, onClose, caseData, routeProviders }: EditGeneralCaseModalProps) => {
  const { mutateAsync: updateCaseMutation } = useUpdateCaseMutation();
  const { updateNrp } = useSetNrp();
  const { mutateAsync: transferFiles } = useReallocationFilesTransfer();
  const { mutateAsync: setQuestionaryEditAllowance } = useQuestionaryEditAllowanceMutation();

  const handleSubmit = async (formData: CaseGeneralFormType) => {
    const prevFormValues = caseGeneralInitialValues(caseData);
    const { caseId } = caseData;

    try {
      const changedValues = getChangedValues<Partial<CaseGeneralFormType>>(formData, prevFormValues);

      const {
        nrp,
        nrpType,
        isQuestionaryEditAllowed: _isQuestionaryEditAllowed,
        laterality: _changedLaterality,
        ...restValues
      } = changedValues;

      const valuesToPatch = {
        ...restValues,
        ...getLateralityPatch(formData.organType, formData.laterality),
      };

      const switchingToDA =
        caseData.serviceType !== ServiceType.DirectAcquisition &&
        formData.serviceType === ServiceType.DirectAcquisition;

      const { nextQuestionaryEditAllowed, hasQuestionaryEditAllowedChanged } = getQuestionaryEditAllowanceChange({
        status: caseData.status,
        prevOrganType: prevFormValues.organType,
        nextOrganType: formData.organType,
        prevTxCenterTrialPrograms: prevFormValues.txCenterTrialPrograms,
        nextTxCenterTrialPrograms: formData.txCenterTrialPrograms,
        prevIsQuestionaryEditAllowed: prevFormValues.isQuestionaryEditAllowed,
        nextIsQuestionaryEditAllowed: formData.isQuestionaryEditAllowed,
      });

      if (switchingToDA) {
        const assigneesUnassign = buildUnassignTxUsersIfDA(
          caseData.serviceType,
          formData.serviceType,
          caseData.assignedCustomers,
        );

        const defaultTxUnassign: Assignee[] = buildDefaultTxUnassign(caseData.assignedCustomers);

        await updateCaseMutation({
          caseId,
          ...replaceEmptyWithNull(valuesToPatch),
          assignees: assigneesUnassign,
          defaultTxAssignees: defaultTxUnassign,
        });
      } else if (Object.keys(valuesToPatch).length > 0) {
        await updateCaseMutation({
          caseId,
          ...replaceEmptyWithNull(valuesToPatch),
        });
      }

      if (hasQuestionaryEditAllowedChanged) {
        await setQuestionaryEditAllowance({
          caseId,
          isQuestionaryEditAllowed: nextQuestionaryEditAllowed,
        });
      }

      if (valuesToPatch.reAllocationCaseId) {
        await transferFiles({ caseId });
      }

      const nrpData = getClinicalRecordByType(caseData.clinicalData, ClinicalItemType.NRPApplicabilityAndType);

      await updateNrp(formData.nrp, formData.nrpType, !isNil(nrp), !isNil(nrpType), caseId, nrpData?.item.id);

      toast.success('Case general data successfully saved.');
      onClose();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80',
        header: 'flex justify-between p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="4xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
    >
      <ModalContent>
        <Form<CaseGeneralFormType>
          defaultValues={caseGeneralInitialValues(caseData)}
          schema={caseGeneralSchema}
          onSubmit={handleSubmit}
        >
          <EditGeneralCaseForm caseData={caseData} routeProviders={routeProviders} />

          <EditCaseModalFooter onClose={onClose} />
        </Form>
      </ModalContent>
    </Modal>
  );
};
